import {
    Autocomplete,
    Button,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Grid,
    TextField
} from '@mui/material';
import { useObservableState } from 'observable-hooks';
import { useEffect, useMemo, useState } from 'react';
import { connect, useDispatch } from 'react-redux';
import { from } from 'rxjs';
import { debounceTime, filter, switchMap, tap } from 'rxjs/operators';
import { AzureAdUserDto, UserClient } from '../../../common/PricePrgClient';
import { ProgressButton } from '../../../common/ProgressButton';
import { UserAvatar } from '../../../common/UserAvatar';
import { RootState } from '../../../store';
import { getUserGroups } from '../../usergroup/redux/userGroupSelectors';
import { UserGroup } from '../../usergroup/redux/userGroupSlices';
import { inviteInternalUser, openInviteInternalUserDialog } from '../redux/userSlices';
const mapStateToProps = (state: RootState) => ({
    groups: getUserGroups(state),
    userDialogModel: state.user.ui.inviteInternalUserDialog
});

type Props = ReturnType<typeof mapStateToProps>;

const userClient = new UserClient();

const InviteUserDialog = ({ groups, userDialogModel }: Props) => {
    const [open, setOpen] = useState(false);
    const [selectedUser, setSelectedUser] = useState(null as AzureAdUserDto | null);
    const [selectedUserGroups, setSelectedUserGroups] = useState([] as number[]);

    const [loading, setLoading] = useState(false);
    const [searchStr, setSearchStr] = useState('');

    const dispatch = useDispatch();

    useEffect(() => {
        setSelectedUser(null);
        setSelectedUserGroups([]);
        setLoading(false);
        setSearchStr('');
    }, []);

    const [users, updateSearchString] = useObservableState<AzureAdUserDto[], string>(
        event$ =>
            event$.pipe(
                tap(searchString => setSearchStr(searchString)),
                filter(searchString => searchString.length > 2),
                tap(() => {
                    setOpen(false);
                    setLoading(true);
                }),
                debounceTime(300),
                switchMap(searchString => from(userClient.searchUnregisteredUsers(searchString))),
                tap(() => {
                    setLoading(false);
                    setOpen(true);
                })
            ),
        []
    );

    const groupsArr = useMemo(() => Object.values(groups), [groups]);

    const handleSubmit = () => {
        if (selectedUser == null) {
            return;
        }

        dispatch(
            inviteInternalUser.request({
                email: selectedUser.username,
                groupIdsToJoin: selectedUserGroups,
                principalId: selectedUser.principalId
            })
        );
    };

    const handleUserSelected = (user: AzureAdUserDto | null) => {
        setSelectedUser(user);
    };

    const handleUserGroupsSelcted = (userGroups: UserGroup[]) => {
        setSelectedUserGroups(userGroups.map(ug => ug.userGroupId));
    };

    const handleClose = () => {
        dispatch(openInviteInternalUserDialog(false));
    };

    return (
        <Dialog
            open={userDialogModel.isOpen}
            onClose={handleClose}
            scroll="paper"
            //check if Paper needs to have this prop
            sx={{ minWidth: '500px', minHeight: '300px' }}
        >
            <DialogTitle>Invite internal user</DialogTitle>
            <DialogContent dividers={true}>
                <Grid container direction="column" spacing={2}>
                    <Grid item>
                        <Autocomplete<AzureAdUserDto>
                            sx={{ width: 300 }}
                            open={open}
                            onOpen={() => {
                                setOpen(true);
                            }}
                            onClose={() => {
                                setOpen(false);
                            }}
                            onChange={(event, value) => handleUserSelected(value)}
                            onInputChange={(event, value, reason) => {
                                if (reason === 'input') {
                                    updateSearchString(value);
                                }
                            }}
                            isOptionEqualToValue={(option, value) => option.principalId === value.principalId}
                            getOptionLabel={user => user.fullName}
                            renderOption={(props, user) => (
                                <li {...props}>
                                    <UserAvatar user={user} iconOnly={false} />
                                </li>
                            )}
                            options={users}
                            loading={loading}
                            noOptionsText={
                                searchStr.length > 2 ? 'No users found' : 'Type more to search for users'
                            }
                            renderInput={params => (
                                <TextField
                                    {...params}
                                    autoFocus
                                    variant="outlined"
                                    placeholder="Search for user"
                                    InputProps={{
                                        ...params.InputProps,
                                        startAdornment: selectedUser != null && (
                                            <UserAvatar user={selectedUser} iconOnly={true} />
                                        ),
                                        endAdornment: (
                                            <>
                                                {loading ? (
                                                    <CircularProgress color="inherit" size={20} />
                                                ) : null}
                                                {params.InputProps.endAdornment}
                                            </>
                                        )
                                    }}
                                />
                            )}
                        />
                    </Grid>
                    <Grid item>
                        <Autocomplete<UserGroup, true>
                            multiple
                            options={groupsArr}
                            getOptionLabel={option => option.name}
                            onChange={(event, value) => handleUserGroupsSelcted(value)}
                            renderInput={params => (
                                <TextField
                                    {...params}
                                    variant="standard"
                                    label="User groups"
                                    placeholder="User groups"
                                />
                            )}
                        />
                    </Grid>
                </Grid>
            </DialogContent>
            <DialogActions>
                <Button onClick={handleClose} color="primary">
                    Cancel
                </Button>
                <ProgressButton
                    color="primary"
                    onClick={handleSubmit}
                    disabled={!selectedUser}
                    isLoading={userDialogModel.isSaving}
                >
                    Invite
                </ProgressButton>
            </DialogActions>
        </Dialog>
    );
};

export default connect(mapStateToProps)(InviteUserDialog);
